/**
 * useLeadDetail — Iter 156.11. Extracted from `LeadDetailPage.jsx`.
 * Owns lead/messages/orders/reminders state plus all mutation handlers.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { leadsApi, messagesApi, ordersApi, remindersApi, usersApi } from "../../lib/api";

export default function useLeadDetail(leadId, user) {
  const navigate = useNavigate();

  const [lead, setLead] = useState(null);
  const [messages, setMessages] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [salespeople, setSalespeople] = useState([]);
  const [loading, setLoading] = useState(true);

  const [newMessage, setNewMessage] = useState("");
  const [sendViaWhatsApp, setSendViaWhatsApp] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);

  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [showReminderDialog, setShowReminderDialog] = useState(false);

  const [orderData, setOrderData] = useState({ order_number: "", amount: "", category: "", notes: "" });
  const [reminderData, setReminderData] = useState({ date: new Date(), message: "" });

  const fetchLead = useCallback(async () => {
    try {
      const response = await leadsApi.getOne(leadId);
      setLead(response.data.lead);
      setMessages(response.data.messages || []);
      setOrders(response.data.orders || []);
      setReminders(response.data.reminders || []);
    } catch {
      toast.error("Failed to load lead");
      navigate("/leads");
    } finally {
      setLoading(false);
    }
  }, [leadId, navigate]);

  useEffect(() => { fetchLead(); }, [fetchLead]);

  useEffect(() => {
    if (user?.role === "admin" || user?.role === "leader" || user?.role === "super_admin") {
      usersApi.getAll().then((res) => {
        setSalespeople(res.data.users.filter((u) => u.role === "salesperson" && u.is_active));
      });
    }
  }, [user]);

  const handleUpdateLead = async (field, value) => {
    try {
      const response = await leadsApi.update(leadId, { [field]: value });
      setLead(response.data.lead);
      toast.success("Lead updated");
    } catch {
      toast.error("Failed to update lead");
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSendingMessage(true);
    try {
      const response = await messagesApi.create({
        lead_id: leadId,
        content: newMessage,
        direction: "outgoing",
        send_whatsapp: sendViaWhatsApp,
      });
      setMessages((prev) => [...prev, response.data.message]);
      setNewMessage("");

      if (sendViaWhatsApp && response.data.whatsapp_result) {
        if (response.data.whatsapp_result.success) {
          toast.success("Message sent via WhatsApp!");
        } else {
          toast.warning("Message saved but WhatsApp delivery failed. Try again later.");
        }
      } else {
        toast.success("Message saved");
      }
    } catch {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    try {
      const response = await ordersApi.create({
        lead_id: leadId,
        ...orderData,
        amount: parseFloat(orderData.amount),
      });
      setOrders((prev) => [response.data.order, ...prev]);
      setShowOrderDialog(false);
      setOrderData({ order_number: "", amount: "", category: "", notes: "" });
      toast.success("Order added successfully");
      fetchLead();
    } catch {
      toast.error("Failed to add order");
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm("Delete this order?")) return;
    try {
      await ordersApi.delete(orderId);
      setOrders((prev) => prev.filter((o) => o.id !== orderId));
      toast.success("Order deleted");
    } catch {
      toast.error("Failed to delete order");
    }
  };

  const handleCreateReminder = async (e) => {
    e.preventDefault();
    try {
      const response = await remindersApi.create({
        lead_id: leadId,
        reminder_date: reminderData.date.toISOString(),
        message: reminderData.message,
      });
      setReminders((prev) => [...prev, response.data.reminder]);
      setShowReminderDialog(false);
      setReminderData({ date: new Date(), message: "" });
      toast.success("Reminder created");
    } catch {
      toast.error("Failed to create reminder");
    }
  };

  const handleCompleteReminder = async (reminderId) => {
    try {
      await remindersApi.complete(reminderId);
      setReminders((prev) => prev.map((r) => (r.id === reminderId ? { ...r, completed: true } : r)));
      toast.success("Reminder completed");
    } catch {
      toast.error("Failed to complete reminder");
    }
  };

  return {
    lead, messages, orders, reminders, salespeople, loading,
    newMessage, setNewMessage, sendViaWhatsApp, setSendViaWhatsApp, sendingMessage,
    showOrderDialog, setShowOrderDialog,
    showReminderDialog, setShowReminderDialog,
    orderData, setOrderData, reminderData, setReminderData,
    handleUpdateLead, handleSendMessage,
    handleCreateOrder, handleDeleteOrder,
    handleCreateReminder, handleCompleteReminder,
  };
}

export const STATUSES = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "follow-up", label: "Follow-up" },
  { value: "negotiating", label: "Negotiating" },
  { value: "converted", label: "Converted" },
  { value: "lost", label: "Lost" },
];

export const CATEGORIES = ["Books", "Sculptures", "Paintings", "Clothing", "Jewelry", "Home and Living"];
